interface MonsWeblabHeader extends Element {
  name: 'a2z:mons_weblab_treatment';
  content?: string;
}

// IDs come from 'src/styles/_weblabs.scss
// TODO: Update IDs as weblabs become available.
// TODO: When experimentation is complete, remove this export and its references.
export const WEBLAB_IDS = {
  group0: 'WEBLAB_PLACEHOLDER_NAME_GROUP_0',
  group1: 'SP_SERVICES_706592_T1',
  group2: 'WEBLAB_PLACEHOLDER_NAME_GROUP_2',
  group3: 'WEBLAB_PLACEHOLDER_NAME_GROUP_3',
  group4: 'WEBLAB_PLACEHOLDER_NAME_GROUP_4',
  group5: 'WEBLAB_PLACEHOLDER_NAME_GROUP_5',
  group6: 'WEBLAB_PLACEHOLDER_NAME_GROUP_6',
};

/**
 * Mons can add <meta> tags to the HTML page indicating that a weblab + treatment is being applied to this page.
 * For weblab-ing visual changes, this converts those tags into a class on the
 * document <body> before KatalComponents are registered to the DOM.
 * ref: https://w.amazon.com/bin/view/Mons/Weblab/Configuration/
 */
export const convertWeblabTagsToCssClasses = () => {
  const weblabMetaTag = document.head.querySelector<MonsWeblabHeader>(
    'meta[name="a2z:mons_weblab_treatment"]'
  );
  if (!weblabMetaTag || !weblabMetaTag.content) {
    return;
  }

  const weblabs = weblabMetaTag.content.split(',');
  weblabs.forEach(weblab => {
    // '=' is not a valid CSS character, so convert to '_'
    const parsedWeblabName = weblab.replace('=', '_');

    document.body.classList.add(parsedWeblabName);
  });
};

// #region Weblab Observers
/**
 * Changes to a <body>'s CSS class won't trigger a re-render of a child component by default.
 *
 * In order to trigger a re-render when a weblab class is added or removed
 * (e.g. when a new icon on a component is under experimentation),
 * attach 'onBodyWeblabClassChange' and 'offBodyWeblabClassChange' to a component's
 * connectedCallback and disconnectedCallback, respectively, passing along a
 * callback to trigger the component's rerender.
 */

type RenderCallback = () => void;

const weblabClassChangeCallbacks = new Set<RenderCallback>();

const includesWeblabClass = (classList: string[] | null) => {
  if (!classList) {
    return false;
  }

  return Object.values(WEBLAB_IDS).some(weblabClass =>
    classList.includes(weblabClass)
  );
};

const notifyBodyWeblabClassChange = (mutations: MutationRecord[]) => {
  mutations.forEach(mutation => {
    const currBodyClassList = Array.from(
      (mutation.target as HTMLElement).classList
    );
    const prevBodyClassList = mutation.oldValue?.split(' ');

    const weblabClassAddedOrRemoved =
      includesWeblabClass(prevBodyClassList) ||
      includesWeblabClass(currBodyClassList);
    if (weblabClassAddedOrRemoved) {
      weblabClassChangeCallbacks.forEach(cb => cb());
    }
  });
};

const observer = new MutationObserver(notifyBodyWeblabClassChange);

let observing = false;
export const onBodyWeblabClassChange = (cb: RenderCallback) => {
  weblabClassChangeCallbacks.add(cb);
  if (!observing) {
    observing = true;

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  }
};

export const offBodyWeblabClassChange = (cb: RenderCallback) => {
  weblabClassChangeCallbacks.delete(cb);
  if (observing) {
    observing = false;
    observer.disconnect();
  }
};

// #endregion
